import { useId } from 'react'
import type {
  LearnerNoticeMaterialType,
  OfflineMaterials,
} from '@/types/configurationV2'
import {
  setLearnerNoticeMaterialType,
  setMaterialAvailability,
} from './offlineMaterials'

interface OfflineMaterialsSettingsProps {
  materials: OfflineMaterials
  onChange: (materials: OfflineMaterials) => void
}

const NOTICE_MATERIAL_OPTIONS: {
  value: LearnerNoticeMaterialType | undefined
  label: string
}[] = [
  { value: undefined, label: '자동 선택 (기본)' },
  { value: 'CHECKUP_BOOK', label: '체크업북' },
  { value: 'WORKBOOK', label: '워크북' },
]

export default function OfflineMaterialsSettings({
  materials,
  onChange,
}: OfflineMaterialsSettingsProps) {
  const id = useId()
  const noticeGroupName = `learner-notice-material-${id}`
  const noticeHelpId = `learner-notice-material-help-${id}`
  const noticeWarningId = `learner-notice-material-warning-${id}`
  const selectedMaterialUnavailable =
    (materials.learnerNoticeMaterialType === 'CHECKUP_BOOK' && !materials.checkupBook)
    || (materials.learnerNoticeMaterialType === 'WORKBOOK' && !materials.workbook)
  const noticeDescriptionIds = selectedMaterialUnavailable
    ? `${noticeHelpId} ${noticeWarningId}`
    : noticeHelpId

  return (
    <fieldset className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <legend className="px-1 text-sm font-bold text-emerald-800">
        오프라인 교재 (트랙)
      </legend>
      <p className="mb-2 text-xs text-emerald-700">
        체크업북·워크북 유무를 코스(트랙) 단위로 설정합니다. 워크북 없음으로 두면 강사 코칭 콘솔에서 워크북 채점이 숨겨집니다.
        (미설정 시 둘 다 있음으로 처리)
      </p>

      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={materials.checkupBook}
            onChange={(event) => {
              onChange(setMaterialAvailability(materials, 'checkupBook', event.target.checked))
            }}
          />
          체크업북 있음
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={materials.workbook}
            onChange={(event) => {
              onChange(setMaterialAvailability(materials, 'workbook', event.target.checked))
            }}
          />
          워크북 있음
        </label>
      </div>

      <fieldset
        className="mt-3 border-t border-emerald-200 pt-3"
        aria-describedby={noticeDescriptionIds}
      >
        <legend className="text-sm font-semibold text-gray-900">
          깨처 펼침 안내 교재
        </legend>
        <p id={noticeHelpId} className="mt-1 text-xs text-gray-600">
          깨처 다시 보기 팝업에서 펼치도록 안내할 교재 한 종류를 결정합니다. 메인 화면의 전체 교재 목록 및 워크북 채점 노출 여부와는 별개입니다.
          자동 선택 시 사용 가능한 교재가 하나면 해당 교재를, 둘 다면 체크업북을 안내합니다.
        </p>
        {selectedMaterialUnavailable ? (
          <p
            id={noticeWarningId}
            role="status"
            className="mt-2 rounded bg-amber-100 px-2 py-1.5 text-xs font-medium text-amber-800"
          >
            선택한 안내 교재가 ‘없음’ 상태여서 깨처 펼침 팝업이 표시되지 않습니다. 교재 유무를 켜거나 다른 안내 교재를 선택해 주세요.
          </p>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
          {NOTICE_MATERIAL_OPTIONS.map((option) => (
            <label
              key={option.value ?? 'unset'}
              className="flex items-center gap-2 text-sm text-gray-800"
            >
              <input
                type="radio"
                name={noticeGroupName}
                checked={materials.learnerNoticeMaterialType === option.value}
                disabled={
                  (option.value === 'CHECKUP_BOOK' && !materials.checkupBook)
                  || (option.value === 'WORKBOOK' && !materials.workbook)
                }
                onChange={() => {
                  onChange(setLearnerNoticeMaterialType(materials, option.value))
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
    </fieldset>
  )
}
